import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, CheckCircle, Lock, Tag, X, Check, Loader2, Sparkles, Shield, Star, Quote } from "lucide-react";
import storycraftLogo from "@/assets/storycraft-logo.png";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { PRICING, PlanTier } from "@/config/pricing";
import { useAuth } from "@/contexts/AuthContext";
import { sendLoopsEvent } from "@/lib/loops";

const tools = [
  { name: "Monetisation AI", desc: "Build complete digital products in one click" },
  { name: "Nexus AI", desc: "Scripts, video ideas, and YouTube optimisation" },
  { name: "Clipping AI", desc: "Turn long videos into viral short-form clips" },
  { name: "Keyword Research AI", desc: "Daily-updated trending keywords and opportunities" },
  { name: "Email List Builder AI", desc: "Lead magnets, opt-in pages, and welcome sequences" },
  { name: "Sales Page Builder AI", desc: "Complete sales pages generated from your product" },
  { name: "Launch Advisor", desc: "Step-by-step product launch guidance" },
];

const planDescriptions: Record<PlanTier, string> = {
  free: "25 credits · Nexus AI + Keyword Research · Limited access",
  starter: "150 credits/mo · Content tools · Community access",
  creator: "600 credits/mo · Build & sell products · Monthly Q&A",
  pro: "2,000 credits/mo · Full suite · Priority support · Weekly reports",
};

const plans: { key: PlanTier; popular?: boolean }[] = [
  { key: "starter" },
  { key: "creator", popular: true },
  { key: "pro" },
];

const Checkout = () => {
  const navigate = useNavigate();
  const { user, subscription } = useAuth();
  const [plan, setPlan] = useState<PlanTier>("creator");
  const [isAnnual, setIsAnnual] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [promoCode, setPromoCode] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<string | null>(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);

  const selectedPlan = PRICING[plan];

  useEffect(() => {
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session?.user);
      setIsLoading(false);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session?.user);
      setIsLoading(false);
    });
    return () => authSub.unsubscribe();
  }, []);

  useEffect(() => {
    if (subscription.subscribed && isAuthenticated) {
      navigate("/app/dashboard");
    }
  }, [subscription.subscribed, isAuthenticated, navigate]);

  // Check if user is eligible for a free trial (no existing subscription)
  const isTrialEligible = isAuthenticated && !subscription.subscribed && subscription.status !== "canceled";

  const handleCheckout = async (asTrial = false) => {
    if (!isAuthenticated) {
      toast.info("Please create an account first to start your free trial.");
      navigate("/auth?mode=signup", { state: { from: "/checkout" } });
      return;
    }
    setIsProcessing(true);
    try {
      const sp = PRICING[plan] as any;
      const priceId = isAnnual ? sp.annualStripePriceId : sp.stripePriceId;
      await supabase.functions.invoke("track-checkout-start", { body: { plan } });

      // Fire Loops event for trial/checkout start
      try {
        const userEmail = user?.email || "";
        if (userEmail) {
          await sendLoopsEvent(userEmail, "trial_started", {
            subscriptionPlan: `${plan}_trial`,
          }, {
            plan,
            trialDays: 7,
          });
        }
      } catch (loopsErr) {
        console.warn("Loops event error (non-blocking):", loopsErr);
      }

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId, isLifetime: false, promoCode: appliedPromo, startTrial: asTrial, planKey: plan },
      });
      if (error) throw error;
      if (data?.url) { window.location.href = data.url; } else { throw new Error("No checkout URL returned"); }
    } catch (error: any) {
      console.error("Checkout error:", error);
      toast.error(error.message || "Failed to start checkout. Please try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const displayPrice = isAnnual ? Math.round((selectedPlan as any).annualPrice / 12) : selectedPlan.price;
  const savings = isAnnual ? (selectedPlan as any).annualSavings : 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={storycraftLogo} alt="AI StoryCraft Studio" className="w-10 h-10 rounded-xl object-contain" />
            <span className="font-display text-xl font-bold text-foreground">AI StoryCraft Studio</span>
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>

        <div className="max-w-3xl mx-auto">
          <h1 className="font-display text-3xl font-bold mb-2 text-center">
            {isTrialEligible ? "Start Your 7-Day Free Trial" : "Choose your plan"}
          </h1>
          <p className="text-muted-foreground mb-8 text-center">
            {isTrialEligible
              ? "Choose your plan below. You won't be charged until your trial ends. Cancel anytime."
              : "Get instant access to all AI StoryCraft Studio tools and training"}
          </p>

          {!isAuthenticated && (
            <div className="rounded-xl border border-primary/50 bg-card p-4 mb-8">
              <p className="text-center text-sm">
                <Link to="/auth?mode=signup" className="text-primary hover:underline font-medium">Create an account</Link>
                {" "}or{" "}
                <Link to="/auth" className="text-primary hover:underline font-medium">sign in</Link>
                {" "}to complete your purchase
              </p>
            </div>
          )}

          {/* Monthly / Annual Toggle */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className={cn("text-sm font-medium", !isAnnual ? "text-foreground" : "text-muted-foreground")}>Monthly</span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={cn(
                "relative w-14 h-7 rounded-full transition-colors",
                isAnnual ? "bg-primary" : "bg-muted"
              )}
            >
              <span className={cn(
                "absolute top-0.5 w-6 h-6 rounded-full bg-white transition-transform",
                isAnnual ? "translate-x-7" : "translate-x-0.5"
              )} />
            </button>
            <span className={cn("text-sm font-medium", isAnnual ? "text-foreground" : "text-muted-foreground")}>
              Annual <span className="text-secondary text-xs font-semibold">Save up to $198</span>
            </span>
          </div>

          {/* Plan Selector */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {plans.map(({ key, popular }) => {
              const p = PRICING[key];
              const isSelected = plan === key;
              const price = isAnnual ? Math.round((p as any).annualPrice / 12) : p.price;
              return (
                <button
                  key={key}
                  onClick={() => setPlan(key)}
                  className={cn(
                    "relative p-5 rounded-xl border text-left transition-all",
                    isSelected ? "border-primary bg-primary/5 ring-2 ring-primary/20" : "border-border hover:border-muted-foreground"
                  )}
                >
                  {popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 text-xs font-semibold rounded-full bg-primary text-primary-foreground flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Popular
                    </span>
                  )}
                  <p className="font-semibold text-foreground text-lg">{p.name}</p>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="font-display text-3xl font-bold">${price}</span>
                    <span className="text-sm text-muted-foreground">/mo</span>
                  </div>
                  {isAnnual && (p as any).annualSavings > 0 && (
                    <p className="text-xs text-secondary mt-1 font-semibold">Save ${(p as any).annualSavings}/year</p>
                  )}
                  <p className="text-sm text-primary mt-1 font-medium">{p.credits.toLocaleString()} credits/mo</p>
                  <p className="text-xs text-muted-foreground mt-1">{planDescriptions[key]}</p>
                </button>
              );
            })}
          </div>

          {/* Promo Code */}
          <div className="rounded-xl border border-border bg-card p-6 mb-8">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Have a promo code?
            </h3>
            {appliedPromo ? (
              <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10 border border-primary/30">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-primary" />
                  <span className="font-medium text-foreground">{appliedPromo}</span>
                  <span className="text-sm text-primary">applied</span>
                </div>
                <button onClick={() => { setAppliedPromo(null); setPromoCode(""); }} className="text-muted-foreground hover:text-foreground transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())} placeholder="Enter code" className="flex-1" disabled={isValidatingPromo} />
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!promoCode.trim()) { toast.error("Please enter a promo code"); return; }
                    setAppliedPromo(promoCode.trim());
                    toast.success("Promo code will be validated at checkout");
                  }}
                  disabled={isValidatingPromo || !promoCode.trim()}
                >
                  {isValidatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                </Button>
              </div>
            )}
          </div>

          {/* What's Included */}
          <div className="rounded-xl border border-border bg-card p-6 mb-8">
            <h3 className="font-semibold text-foreground mb-4">What's included:</h3>
            <ul className="space-y-3">
              {tools.map((tool, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-foreground font-medium">{tool.name}</span>
                    <span className="text-muted-foreground"> — {tool.desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Testimonial */}
          <div className="rounded-xl border border-border bg-card p-6 mb-8">
            <div className="flex items-start gap-3">
              <Quote className="w-8 h-8 text-primary flex-shrink-0 opacity-50" />
              <div>
                <p className="text-foreground italic mb-3">
                  "I had 1,200 followers and made $1,247 in my first 14 days. The AI built the whole product for me."
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-secondary fill-secondary" />)}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    — @fitnesswith_jas, <span className="text-secondary font-semibold">1,200 followers</span>, Fitness niche
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Guarantee */}
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-6 mb-8 text-center">
            <Shield className="w-10 h-10 text-primary mx-auto mb-3" />
            <h3 className="font-display font-bold text-lg mb-2">Satisfaction Guarantee</h3>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">
              The 60-Day First Results Guarantee: If you don't make your first sale or get your first 1,000 views within 60 days, our team will personally review your content, product, and strategy — and tell you exactly what to change. We don't offer refunds because we don't need to. We fix the problem.
            </p>
          </div>

          {/* Social Proof */}
          <div className="rounded-xl border border-border bg-card/50 p-5 mb-8">
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm text-muted-foreground mb-4">
              <span className="font-medium text-foreground">Join 4,200+ creators already monetising their audience</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-xs text-muted-foreground">
              <span>$2.1M+ generated by our users</span>
              <span className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 text-secondary fill-secondary" />)}
                4.9/5 from 847 reviews
              </span>
            </div>
          </div>

          {/* Payment Methods Accepted */}
          <div className="rounded-xl border border-border bg-card p-5 mb-8">
            <p className="text-sm text-muted-foreground text-center mb-3">Accepted payment methods</p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <span className="text-xs font-medium text-foreground px-3 py-1.5 rounded-md border border-border bg-background">💳 Card</span>
              <span className="text-xs font-medium text-foreground px-3 py-1.5 rounded-md border border-border bg-background"> Apple Pay</span>
              <span className="text-xs font-medium text-foreground px-3 py-1.5 rounded-md border border-border bg-background">G Pay</span>
              {(plan === "creator" || plan === "pro") && (
                <span className="text-xs font-medium text-foreground px-3 py-1.5 rounded-md border border-border bg-background">Klarna</span>
              )}
            </div>
            {(plan === "creator" || plan === "pro") && (
              <p className="text-xs text-muted-foreground text-center mt-3">
                Pay in 3 interest-free instalments with Klarna · {plan === "creator" ? "3 × $23/mo" : "3 × $33/mo"}
              </p>
            )}
          </div>

          {/* Checkout Button */}
          <div className="space-y-4">
            {isTrialEligible ? (
              <>
                <Button onClick={() => handleCheckout(true)} variant="hero" size="xl" className="w-full" disabled={isProcessing}>
                  {isProcessing ? "Processing..." : `Start My Free Trial — ${selectedPlan.name} $${displayPrice}/mo`}
                </Button>
                <div className="space-y-2 text-center">
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Lock className="w-4 h-4" />
                    <span>Secured by Stripe — your card details are never stored on our servers</span>
                  </div>
                  <p className="text-sm text-muted-foreground">✓ No charge today &nbsp;·&nbsp; ✓ Cancel anytime before {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  <p className="text-sm text-muted-foreground">✓ Instant access to all {selectedPlan.name} features</p>
                </div>
              </>
            ) : (
              <>
                <Button onClick={() => handleCheckout(false)} variant="hero" size="xl" className="w-full" disabled={isProcessing}>
                  {isProcessing ? "Processing..." : `Get ${selectedPlan.name} — $${displayPrice}/mo${isAnnual && savings > 0 ? ` (save $${savings}/yr)` : ''}`}
                </Button>
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <Lock className="w-4 h-4" />
                  <span>Secure checkout powered by Stripe</span>
                </div>
                <p className="text-center text-xs text-muted-foreground">
                  Cancel anytime. Credits roll over monthly.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
